import { useEffect, useState } from "react";
import { errorMessage } from "../../lib/format";
import { Markdown } from "../Markdown";
import { MarkdownEditor } from "../MarkdownEditor";

export function EditableTitle({
  value,
  onSave,
  prefix,
}: {
  value: string;
  onSave: (v: string) => Promise<unknown>;
  prefix?: string;
}) {
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  useEffect(() => setDraft(value), [value]);

  const commit = async () => {
    const v = draft.trim();
    if (!v || v === value) {
      setDraft(value);
      return;
    }
    setSaving(true);
    try {
      await onSave(v);
    } catch {
      setDraft(value);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="editable-title">
      {prefix && <span className="title-prefix">{prefix}</span>}
      <textarea
        className="title-input"
        rows={1}
        value={draft}
        disabled={saving}
        placeholder="Untitled"
        aria-label="Title"
        onChange={(e) => setDraft(e.target.value.replace(/\n/g, ""))}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            (e.target as HTMLTextAreaElement).blur();
          } else if (e.key === "Escape") {
            setDraft(value);
            e.stopPropagation();
          }
        }}
        ref={(el) => {
          if (el) {
            el.style.height = "auto";
            el.style.height = `${el.scrollHeight}px`;
          }
        }}
      />
    </div>
  );
}

/** Click-to-edit markdown block (Notion-like). */
export function EditableMarkdown({
  value,
  onSave,
  placeholder = "Empty. Click to add.",
  label,
}: {
  value: string;
  onSave: (v: string) => Promise<unknown>;
  placeholder?: string;
  label: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = () => {
    setDraft(value);
    setError(null);
    setEditing(true);
  };
  const save = async () => {
    if (draft === value) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(draft);
      setEditing(false);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="doc-section">
      <h3 className="section-label">{label}</h3>
      {editing ? (
        <div className="editable-md-editing">
          <MarkdownEditor value={draft} onChange={setDraft} rows={8} autoFocus onSubmitShortcut={save} />
          {error && <div className="error-box">{error}</div>}
          <div className="row-actions">
            <button className="btn btn-sm" onClick={() => setEditing(false)} disabled={saving}>
              Cancel
            </button>
            <button className="btn btn-sm btn-primary" onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      ) : (
        <div
          className="editable-md"
          role="button"
          tabIndex={0}
          title="Click to edit"
          onClick={(e) => {
            if ((e.target as HTMLElement).closest("a, input")) return;
            start();
          }}
          onKeyDown={(e) => e.key === "Enter" && e.target === e.currentTarget && start()}
        >
          {value.trim() ? <Markdown>{value}</Markdown> : <p className="muted placeholder">{placeholder}</p>}
        </div>
      )}
    </section>
  );
}
